import { computed, Ref, ComputedRef } from 'vue';
import { cloneDeep } from 'lodash-es';
import {
  MAXIMUM_STORED_HISTORY_SIZE,
  TOKEN_STATUS,
} from '@/common/components/molecules/filterSearch/filterSearch.define';
import {
  EditingFilterToken,
  FilterHistory,
  FilterToken,
} from '@/common/components/molecules/filterSearch/filterSearch.type';

interface UseFilterHistoryParams {
  filterHistory: Ref<FilterHistory[]>;
  tokenList: Ref<FilterToken[]>;
  editingToken: Ref<EditingFilterToken | null>;
  lastToken: ComputedRef<FilterToken>;
  saveHistoryToEnv: () => Promise<void>;
}

export function useFilterHistory({
  filterHistory,
  tokenList,
  editingToken,
  lastToken,
  saveHistoryToEnv,
}: UseFilterHistoryParams) {
  const isShowFilterHistoryList = computed<boolean>(() => {
    const isDraftEditingToken =
      editingToken.value?.status === TOKEN_STATUS.KEY_ING && !editingToken.value?.key?.id;

    const effectiveLastToken = isDraftEditingToken
      ? tokenList.value[tokenList.value.length - 2]
      : lastToken.value;
    const effectiveLastTokenStatus = effectiveLastToken?.status;

    return !!(
      filterHistory.value.length &&
      (!effectiveLastTokenStatus || effectiveLastTokenStatus === TOKEN_STATUS.VALUE_DONE) &&
      (!editingToken.value || isDraftEditingToken)
    );
  });

  const isDuplicatedTokenInHistory = ({ key, operator, values }: FilterToken): boolean => {
    return !!filterHistory.value.find(
      ({ token: historyToken }) =>
        historyToken.key.id === key.id &&
        historyToken?.operator?.id === operator?.id &&
        JSON.stringify(historyToken.values) === JSON.stringify(values),
    );
  };

  const saveHistory = async (targetToken: FilterToken) => {
    if (!targetToken) {
      return;
    }

    if (isDuplicatedTokenInHistory(targetToken)) {
      return;
    }

    if (filterHistory.value.length >= MAXIMUM_STORED_HISTORY_SIZE) {
      filterHistory.value.shift();
    }

    filterHistory.value.push({
      token: cloneDeep(targetToken),
      createdTime: Date.now(),
    });

    await saveHistoryToEnv();
  };

  const removeHistoryItem = async (targetHistory: FilterHistory) => {
    const index = filterHistory.value.findIndex(
      ({ createdTime }) => createdTime === targetHistory.createdTime,
    );
    if (index > -1) {
      filterHistory.value.splice(index, 1);
      await saveHistoryToEnv();
    }
  };

  return {
    filterHistory,
    isShowFilterHistoryList,
    saveHistory,
    removeHistoryItem,
  };
}
